import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, Clock, Package, Sparkles as SparklesIcon, MessageSquareHeart, Leaf, Flame, HeartHandshake, ArrowRight, Gift, X as XIcon, Video, Radio, Compass, Sparkle, Waves } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import PaymentMethodDialog from "@/components/PaymentMethodDialog";
import WellnessDisclaimer from "@/components/WellnessDisclaimer";
import Seo from "@/components/Seo";
import { GuerisseurIntro, GuerisseurSections } from "@/components/GuerisseurIntergalactique";
import guerisseur from "@/content/guerisseurIntergalactique.json";

const PROGRAM_SESSION_ID = "guidance"; // "Programme accéléré" is stored under this session type id
const PROGRAM_SESSION_ID_DUP = null; // (kept blank — SLOTS constant removed, slots now come from /api/availability/slots)

function fmtDay(d) {
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function BookingPage() {
    const [sessions, setSessions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [date, setDate] = useState(new Date(Date.now() + 24 * 3600 * 1000));
    const [slot, setSlot] = useState("");
    const [notes, setNotes] = useState("");
    const [packs, setPacks] = useState([]);
    const [programs, setPrograms] = useState({});
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState(null);
    const [codeInput, setCodeInput] = useState("");
    const [codeApplied, setCodeApplied] = useState(null); // {code, message} when validated
    const [showCode, setShowCode] = useState(false);
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    // Fetch live availability slots whenever date or session changes.
    // IMPORTANT: build YYYY-MM-DD from LOCAL date components, never
    // `toISOString().slice(0,10)` — in UTC+N the latter shifts the date
    // back by one day (bug observed in CEST: picking "Fri 2 Oct" sent
    // "2026-10-01" to the backend, giving Thursday's slots).
    useEffect(() => {
        if (!selected || !date || selected === PROGRAM_SESSION_ID) { setSlots([]); return; }
        const d = new Date(date);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        setSlotsLoading(true);
        setSlot("");
        http.get(`/availability/slots`, { params: { date: iso, session_type_id: selected } })
            .then((r) => setSlots(r.data.slots || []))
            .catch(() => setSlots([]))
            .finally(() => setSlotsLoading(false));
    }, [selected, date]);

    useEffect(() => {
        http.get("/sessions/types").then((r) => setSessions(r.data));
        http.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
        if (user && user !== false) {
            http.get("/packs/mine").then((r) => setPacks(r.data)).catch(() => {});
        }
    }, [user]);

    const isProgramme = selected === PROGRAM_SESSION_ID;
    const activePack = packs.find(
        (p) => !p.is_expired && p.sessions_remaining > 0 && p.session_type_id === selected
    );
    const isAccompagnementSubscriber = user && user !== false && (
        user.active_subscription === "accompagnement_essentiel" ||
        user.active_subscription === "accompagnement_profond"
    );

    const purchaseProgram = async () => {
        if (!user || user === false) {
            toast.info("Créez un compte pour acheter le programme.");
            navigate("/inscription");
            return;
        }
        const p = programs["programme_accelere"];
        if (!p) return;
        setDialog({
            kind: "program",
            program_key: "programme_accelere",
            item_name: p.name,
            amount: p.amount,
            origin_url: window.location.origin,
        });
    };

    const programStripeCheckout = async () => {
        const { data } = await http.post("/programs/purchase", {
            program_key: "programme_accelere",
            origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
    };

    const bookingStripeCheckout = async () => {
        const { data } = await http.post("/payments/checkout", {
            booking_id: dialog.booking_id,
            origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
    };

    const submitBooking = async () => {
        if (!user || user === false) {
            toast.info("Veuillez vous connecter pour réserver.");
            navigate("/connexion");
            return;
        }
        if (!selected || !slot) {
            toast.error("Choisissez un soin, une date et un créneau.");
            return;
        }
        try {
            const dt = new Date(date);
            const [h, m] = slot.split(":");
            dt.setHours(+h, +m, 0, 0);
            const payload = {
                session_type_id: selected,
                slot_datetime: dt.toISOString(),
                notes,
            };
            if (codeApplied) payload.discount_code = codeApplied.code;
            const { data: booking } = await http.post("/bookings", payload);
            if (booking.status === "confirmed" && booking.price_paid === 0) {
                toast.success(codeApplied
                    ? `🎁 Séance offerte confirmée grâce au code ${codeApplied.code}`
                    : "Séance décomptée de votre Programme accéléré");
                navigate("/espace");
                return;
            }
            setDialog({
                kind: "booking",
                booking_id: booking.id,
                item_name: `Réservation : ${booking.session_name}`,
                amount: Math.round(booking.price_paid * 100),
                origin_url: window.location.origin,
            });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const applyCode = async () => {
        const trimmed = (codeInput || "").trim();
        if (!trimmed) return;
        try {
            const { data } = await http.post("/discount-codes/validate", {
                code: trimmed,
                scope: "booking",
            });
            setCodeApplied({ code: data.code, message: data.message });
            toast.success(`Code ${data.code} appliqué — séance offerte 🎁`);
        } catch (e) {
            setCodeApplied(null);
            toast.error(formatApiError(e));
        }
    };
    const removeCode = () => { setCodeApplied(null); setCodeInput(""); };

    const prog = programs["programme_accelere"];

    return (
        <div className="max-w-6xl mx-auto px-6 py-20" data-testid="booking-page">
            <Seo
                title={guerisseur.seo.title}
                description={guerisseur.seo.description}
                path="/reservation"
                image={guerisseur.seo.image}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@graph": [{
                    "@type": "Service",
                    "name": "Soin de lumière — soin énergétique par le langage de lumière",
                    "serviceType": "Soin énergétique · Langage de lumière · Messages canalisés · Guidance spirituelle",
                    "provider": {
                        "@type": "Organization",
                        "name": "Foundation of Light",
                        "url": "https://foundation-of-light.com",
                    },
                    "areaServed": "Worldwide",
                    "description": "Séances individuelles à distance : soin de lumière (soin énergétique par le langage de lumière qui contourne le mental pour aller chercher les mémoires profondes) et soin de Connexion aux guides (messages canalisés et codes de lumière personnalisés). Foundation of Light — Olivier Masurel.",
                    "hasOfferCatalog": {
                        "@type": "OfferCatalog",
                        "name": "Séances individuelles Foundation of Light",
                        "itemListElement": [
                            {
                                "@type": "Offer",
                                "itemOffered": {
                                    "@type": "Service",
                                    "name": "Soin de lumière",
                                    "description": "Soin énergétique par le langage de lumière — nettoyage du champ énergétique, libération des mémoires cellulaires et karmiques, réalignement des corps subtils.",
                                },
                            },
                            {
                                "@type": "Offer",
                                "itemOffered": {
                                    "@type": "Service",
                                    "name": "Soin de Connexion aux guides",
                                    "description": "Messages canalisés de vos guides personnels et êtres de lumière, transmission de codes de lumière personnalisés pour activer votre propre langage de lumière.",
                                },
                            },
                        ],
                    },
                    }, {
                        "@type": "FAQPage",
                        "mainEntity": guerisseur.faq.map((f) => ({
                            "@type": "Question",
                            "name": f.q,
                            "acceptedAnswer": { "@type": "Answer", "text": f.a },
                        })),
                    }],
                }}
            />
            <div className="mb-12">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">{guerisseur.eyebrow}</div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-tight" data-testid="booking-h1">{guerisseur.h1}</h1>
                <p className="mt-4 text-sm uppercase tracking-widest text-secondary font-medium">{guerisseur.kicker}</p>
                {activePack && (
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-secondary/15 text-secondary-foreground px-4 py-2 text-sm" data-testid="pack-credits-badge">
                        <Package className="w-4 h-4 text-secondary" />
                        <span>
                            <strong>{activePack.sessions_remaining}</strong>/{activePack.sessions_total} séance
                            {activePack.sessions_remaining > 1 ? "s" : ""} restante
                            {activePack.sessions_remaining > 1 ? "s" : ""} — valable jusqu'au {fmtDay(activePack.expires_at)}
                        </span>
                    </div>
                )}
                {isAccompagnementSubscriber && !activePack && (
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary/15 text-primary px-4 py-2 text-sm" data-testid="accompagnement-discount-badge">
                        <SparklesIcon className="w-4 h-4" />
                        Remise abonné : -10€ appliqués automatiquement sur cette séance.
                    </div>
                )}
                {!isAccompagnementSubscriber && !activePack && (
                    <p className="mt-4 text-sm text-muted-foreground max-w-2xl" data-testid="accompagnement-hint">
                        Les personnes ayant souscrit un accompagnement bénéficient d'une remise automatique de 10€ sur chaque soin ou séance de connexion à ses guides.{" "}
                        <a
                            href="/accompagnements"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline underline-offset-4 hover:text-primary/80"
                            data-testid="accompagnement-link"
                        >
                            Découvrir les accompagnements ↗
                        </a>
                    </p>
                )}
            </div>

            <GuerisseurIntro />

            <h2 id="choisir-soin" className="font-serif text-3xl sm:text-4xl mb-6 scroll-mt-28" data-testid="choose-soin-heading">Choisir mon soin</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
                {sessions.map((s) => {
                    const isProg = s.id === PROGRAM_SESSION_ID;
                    const hasCredit = packs.find(
                        (p) => !p.is_expired && p.sessions_remaining > 0 && p.session_type_id === s.id
                    );
                    return (
                        <button
                            key={s.id}
                            onClick={() => setSelected(s.id)}
                            className={`text-left rounded-3xl border p-6 transition-all ${
                                selected === s.id ? "border-primary bg-accent shadow-lg" : "border-border/60 bg-card hover:border-primary/40"
                            }`}
                            data-testid={`select-session-${s.id}`}
                        >
                            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-4">
                                <img src={s.image_url} alt={s.name} className="w-full h-full object-cover" />
                            </div>
                            <h3 className="font-serif text-2xl mb-2">{s.name}</h3>
                            <p className="text-sm text-foreground/70 mb-4">{s.description}</p>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.duration_minutes} min</span>
                                <span className="font-serif text-lg">
                                    {hasCredit && !isProg ? "Inclus" : `${s.price}€`}
                                </span>
                            </div>
                            {!isProg && (
                                <div className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-white bg-primary rounded-full px-3 py-1.5 shadow-sm" data-testid={`recording-badge-${s.id}`}>
                                    <Video className="w-3.5 h-3.5" strokeWidth={2.5} /> Enregistrement inclus
                                </div>
                            )}
                            {isProg && (
                                <div className="mt-3 text-xs text-secondary inline-flex items-center gap-1">
                                    <SparklesIcon className="w-3 h-3" /> 4 soins de lumière · 1 mois
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* CTA under the 3 session vignettes — /offrir moved out of the top
                nav, so keep it discoverable for anyone considering a gift. */}
            {!isProgramme && (
                <div className="flex justify-center mb-12 -mt-4">
                    <Link
                        to="/offrir"
                        className="group inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                        data-testid="offer-gift-link"
                    >
                        <Gift className="w-4 h-4" />
                        <span>Envie de <strong className="text-foreground group-hover:text-primary">l'offrir à un proche</strong> ? Créez une carte-cadeau en 2 minutes</span>
                        <ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </Link>
                </div>
            )}

            {/* Ce à quoi s'attendre — moved from Landing */}

            {isProgramme && prog && (
                <div className="rounded-3xl border border-secondary/60 bg-secondary/5 p-8 shadow-xl" data-testid="program-purchase-card">
                    <h2 className="font-serif text-3xl mb-3">{prog.name}</h2>
                    <p className="text-foreground/80 leading-relaxed mb-6">{prog.description}</p>
                    <ul className="space-y-2 text-sm mb-8">
                        <li className="flex items-start gap-2"><SparklesIcon className="w-4 h-4 text-primary mt-0.5" /> {prog.sessions_count} séances de soins de lumière incluses</li>
                        <li className="flex items-start gap-2"><SparklesIcon className="w-4 h-4 text-primary mt-0.5" /> Réservation créneau par créneau depuis votre espace</li>
                        <li className="flex items-start gap-2"><SparklesIcon className="w-4 h-4 text-primary mt-0.5" /> Valable {prog.validity_days} jours à partir de l'achat</li>
                        <li className="flex items-start gap-2"><SparklesIcon className="w-4 h-4 text-primary mt-0.5" /> Décompte automatique à chaque réservation de soins de lumière</li>
                    </ul>
                    <div className="flex flex-wrap items-baseline gap-3 mb-6">
                        <span className="font-serif text-5xl">{(prog.amount / 100).toFixed(0)}€</span>
                        <span className="text-sm text-muted-foreground">soit {(prog.amount / prog.sessions_count / 100).toFixed(0)}€ / séance</span>
                    </div>
                    <Button
                        onClick={purchaseProgram}
                        disabled={busy}
                        className="rounded-full h-12 px-8 bg-secondary text-secondary-foreground hover:scale-[1.02] transition-transform"
                        data-testid="program-buy"
                    >
                        {busy ? "Redirection…" : "Acheter le Programme accéléré"}
                    </Button>
                </div>
            )}

            {selected && !isProgramme && (
                <div className="grid md:grid-cols-2 gap-10 rounded-3xl border border-border/60 bg-card p-8" data-testid="booking-form">
                    <div>
                        <h3 className="font-serif text-2xl mb-4 flex items-center gap-2"><CalendarIcon className="w-5 h-5" /> Date</h3>
                        <Calendar
                            mode="single"
                            selected={date}
                            onSelect={(d) => d && setDate(d)}
                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                            className="rounded-2xl border border-border/60"
                            data-testid="booking-calendar"
                        />
                    </div>
                    <div>
                        <h3 className="font-serif text-2xl mb-4">Créneau</h3>
                        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="slots-grid">
                            {slotsLoading && <span className="col-span-3 text-sm text-muted-foreground">Chargement des créneaux…</span>}
                            {!slotsLoading && slots.length === 0 && (
                                <span className="col-span-3 text-sm text-muted-foreground" data-testid="no-slots">
                                    Aucun créneau disponible ce jour. Choisissez une autre date.
                                </span>
                            )}
                            {!slotsLoading && slots.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setSlot(s)}
                                    className={`rounded-full py-2.5 text-sm border transition-all ${
                                        slot === s ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"
                                    }`}
                                    data-testid={`slot-${s.replace(":", "")}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <label className="block text-sm mb-2">Notes (optionnel)</label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Ce que vous souhaitez partager avant la séance…"
                            className="rounded-2xl mb-4"
                            data-testid="booking-notes"
                        />
                        {/* Discount code */}
                        <div className="mb-6" data-testid="booking-code-block">
                            {!showCode && !codeApplied && (
                                <button
                                    type="button"
                                    onClick={() => setShowCode(true)}
                                    className="text-sm text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary inline-flex items-center gap-1.5"
                                    data-testid="booking-show-code"
                                >
                                    <Gift className="w-4 h-4" /> J'ai un code cadeau
                                </button>
                            )}
                            {showCode && !codeApplied && (
                                <div className="flex gap-2 items-end">
                                    <div className="flex-1">
                                        <label className="block text-xs text-muted-foreground mb-1">Code cadeau</label>
                                        <input
                                            value={codeInput}
                                            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                                            placeholder="EX : OFFERT2026"
                                            className="w-full h-11 rounded-full border border-input bg-background px-4 text-sm font-mono uppercase tracking-wider"
                                            data-testid="booking-code-input"
                                        />
                                    </div>
                                    <Button type="button" onClick={applyCode} className="rounded-full h-11 px-5" data-testid="booking-code-apply">Valider</Button>
                                </div>
                            )}
                            {codeApplied && (
                                <div className="flex items-center justify-between gap-3 rounded-2xl border border-secondary/40 bg-secondary/10 px-4 py-3" data-testid="booking-code-applied">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Gift className="w-4 h-4 text-secondary" />
                                        <span>Code <strong className="font-mono">{codeApplied.code}</strong> appliqué — séance offerte 🎁</span>
                                    </div>
                                    <button type="button" onClick={removeCode} className="p-1 rounded-full hover:bg-secondary/20" data-testid="booking-code-remove">
                                        <XIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                        <Button
                            onClick={submitBooking}
                            className="w-full rounded-full h-12 bg-primary text-primary-foreground hover:scale-[1.02] transition-transform"
                            data-testid="booking-submit"
                        >
                            {codeApplied ? "Confirmer ma séance offerte 🎁" : (activePack ? "Confirmer avec mon Programme" : "Réserver et payer")}
                        </Button>
                    </div>
                </div>
            )}
            <PaymentMethodDialog
                open={!!dialog}
                onOpenChange={(o) => !o && setDialog(null)}
                item={dialog}
                stripeCheckout={dialog?.kind === "program" ? programStripeCheckout : bookingStripeCheckout}
            />

            <GuerisseurSections />

            {/* Votre chemin — SEO-rich section covering both soin types (light + guides connection) */}
            <section className="relative overflow-hidden py-16 mt-12" data-testid="what-to-expect">
                <div className="max-w-6xl mx-auto">
                    <div className="max-w-3xl mb-14">
                        <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Votre chemin</div>
                        <h2 className="font-serif text-4xl sm:text-5xl mb-6">Deux portes d'entrée, une même fréquence</h2>
                        <p className="text-lg text-foreground/70 leading-relaxed">
                            Le <strong>soin énergétique avec le langage de lumière</strong> est une pratique subtile où la fréquence vibratoire
                            parle directement à l'âme, sans passer par le mental. Selon ce que vous traversez, deux formes de séances
                            individuelles vous sont proposées — l'une pour libérer et réaligner, l'autre pour recevoir des
                            <em> messages canalisés</em> et des codes de lumière personnalisés.
                        </p>
                    </div>

                    {/* Two soin cards — SEO anchors: soin de lumière · soin de connexion aux guides */}
                    <div className="grid md:grid-cols-2 gap-8 mb-20" data-testid="soins-presentation">
                        {/* SOIN DE LUMIÈRE */}
                        <article
                            className="relative rounded-3xl bg-card border border-border/60 p-10 flex flex-col group hover:border-primary/40 hover:shadow-lg transition-all"
                            data-testid="soin-lumiere-card"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Waves className="w-6 h-6 text-primary" />
                                </div>
                                <h3 className="font-serif text-2xl sm:text-3xl">Soin de lumière</h3>
                            </div>
                            <p className="text-sm uppercase tracking-widest text-secondary mb-4 font-medium">
                                Soin énergétique · Libération · Réalignement
                            </p>
                            <p className="text-foreground/80 leading-relaxed mb-4">
                                Le <strong>soin de lumière</strong> s'appuie sur le langage de lumière — cette fréquence qui contourne
                                le mental, les défenses et les récits pour parler directement à la <strong>mémoire cellulaire</strong>,
                                à l'âme, aux couches les plus anciennes de l'être. Le champ énergétique est nettoyé, les tensions se
                                dissolvent, et les mémoires non résolues — celles que d'autres approches n'ont pas pu atteindre —
                                deviennent accessibles.
                            </p>
                            <p className="text-foreground/80 leading-relaxed mb-4">
                                Pendant chaque séance, je suis accompagné d'<strong>êtres de lumière</strong> qui voient ce que je ne vois
                                pas, qui interviennent là où je n'ai pas accès, et qui ajustent le rythme selon ce que votre être peut
                                recevoir.
                            </p>
                            <p className="text-foreground/70 text-sm italic border-l-2 border-primary/30 pl-4 mb-6">
                                « Mais j'avais déjà travaillé ça » — c'est la phrase que j'entends le plus souvent en fin de première
                                séance. Le travail par couches, ce n'est pas invalider ce qui a été fait avant ; c'est révéler la couche
                                suivante que le travail précédent a rendue accessible.
                            </p>
                            <div className="mt-auto pt-4">
                                <p className="text-sm text-muted-foreground mb-2 font-medium">Particulièrement indiqué si :</p>
                                <ul className="text-sm text-foreground/70 space-y-1.5">
                                    <li className="flex items-start gap-2"><Sparkle className="w-3.5 h-3.5 text-primary mt-1 shrink-0" /> Vous cherchez un mieux-être rapide face à une période difficile</li>
                                    <li className="flex items-start gap-2"><Sparkle className="w-3.5 h-3.5 text-primary mt-1 shrink-0" /> Vous avez traversé un traumatisme, un deuil ou un choc émotionnel</li>
                                    <li className="flex items-start gap-2"><Sparkle className="w-3.5 h-3.5 text-primary mt-1 shrink-0" /> Vous souhaitez élever votre taux vibratoire et vous transformer en profondeur</li>
                                </ul>
                            </div>
                            <a
                                href="/journal/soin-de-lumiere-soin-energetique-langage-de-lumiere"
                                className="mt-6 inline-flex items-center gap-1 text-sm text-primary hover:underline underline-offset-4"
                                data-testid="soin-lumiere-learn-more"
                            >
                                Lire l'article complet sur le soin de lumière <ArrowRight className="w-3.5 h-3.5" />
                            </a>
                        </article>

                        {/* SOIN DE CONNEXION AUX GUIDES */}
                        <article
                            className="relative rounded-3xl bg-card border border-border/60 p-10 flex flex-col group hover:border-secondary/50 hover:shadow-lg transition-all"
                            data-testid="soin-connexion-guides-card"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 rounded-full bg-secondary/15 flex items-center justify-center">
                                    <Radio className="w-6 h-6 text-secondary" />
                                </div>
                                <h3 className="font-serif text-2xl sm:text-3xl">Soin de Connexion aux guides</h3>
                            </div>
                            <p className="text-sm uppercase tracking-widest text-secondary mb-4 font-medium">
                                Messages canalisés · Guidance · Codes de lumière personnalisés
                            </p>
                            <p className="text-foreground/80 leading-relaxed mb-4">
                                Le <strong>soin de Connexion aux guides</strong> est un espace de réception. Il vous permet de
                                recevoir des <strong>messages canalisés</strong> de vos guides personnels — ou d'autres êtres de lumière
                                qui souhaitent intervenir selon ce qui est juste dans le moment. Ces messages viennent éclairer un
                                questionnement, confirmer une intuition, poser un jalon sur votre chemin.
                            </p>
                            <p className="text-foreground/80 leading-relaxed mb-4">
                                La séance intègre également la transmission de <strong>codes de lumière personnalisés</strong> —
                                des séquences vibratoires reçues spécifiquement pour vous, qui peuvent servir de clés pour
                                <strong> activer votre propre langage de lumière</strong>, ouvrir des accès à des mémoires d'âme,
                                ou soutenir une transformation en cours.
                            </p>
                            <p className="text-foreground/70 text-sm italic border-l-2 border-secondary/30 pl-4 mb-6">
                                Vos guides savent bien mieux que nous ce dont vous avez besoin, ici et maintenant. Ma seule tâche
                                est de tenir l'espace, de traduire quand cela est demandé, et de rendre ces transmissions accessibles
                                dans votre langue et votre fréquence.
                            </p>
                            <div className="mt-auto pt-4">
                                <p className="text-sm text-muted-foreground mb-2 font-medium">Particulièrement indiqué si :</p>
                                <ul className="text-sm text-foreground/70 space-y-1.5">
                                    <li className="flex items-start gap-2"><Sparkle className="w-3.5 h-3.5 text-secondary mt-1 shrink-0" /> Vous ressentez l'appel d'une guidance à un moment charnière</li>
                                    <li className="flex items-start gap-2"><Sparkle className="w-3.5 h-3.5 text-secondary mt-1 shrink-0" /> Vous souhaitez activer ou approfondir votre propre langage de lumière</li>
                                    <li className="flex items-start gap-2"><Sparkle className="w-3.5 h-3.5 text-secondary mt-1 shrink-0" /> Vous cherchez à clarifier une décision ou une direction spirituelle</li>
                                </ul>
                            </div>
                            <a
                                href="/langage-de-lumiere"
                                className="mt-6 inline-flex items-center gap-1 text-sm text-secondary hover:underline underline-offset-4"
                                data-testid="soin-connexion-learn-more"
                            >
                                En savoir plus sur le langage de lumière <ArrowRight className="w-3.5 h-3.5" />
                            </a>
                        </article>
                    </div>

                    {/* Ce à quoi s'attendre — 4-step universal flow (works for both soin types) */}
                    <div className="max-w-2xl mb-12">
                        <h3 className="font-serif text-3xl sm:text-4xl mb-4">Ce à quoi s'attendre</h3>
                        <p className="text-lg text-foreground/70 leading-relaxed">
                            Quelle que soit la porte d'entrée que vous choisissez, la séance se vit dans la simplicité :
                            vous n'avez rien à préparer, ni à comprendre. Voici les quatre temps qui rythment votre rencontre,
                            à distance via Zoom.
                        </p>
                    </div>

                    <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            {
                                n: "01",
                                icon: MessageSquareHeart,
                                title: "La réservation & le lien",
                                text: "Vous choisissez le jour et l'heure qui vous conviennent. Après votre réservation, vous recevez un lien Zoom et quelques instructions pratiques — aucune préparation particulière n'est demandée.",
                            },
                            {
                                n: "02",
                                icon: Leaf,
                                title: "Votre installation",
                                text: "Idéalement allongé·e confortablement (canapé, lit, table de soin), les yeux fermés pour faciliter le voyage intérieur. Dans un environnement calme. Si vous avez besoin de bouger, une occupation douce est également possible — l'énergie fait son œuvre.",
                            },
                            {
                                n: "03",
                                icon: Flame,
                                title: "Le début de séance",
                                text: "Nous échangeons d'abord quelques mots, si vous le souhaitez, sur ce qui vous amène. Puis vient la transmission (code, lumière, énergie et éventuels messages canalisés) — votre micro coupé et caméra active. Vous pouvez décider de ne pas entendre le langage de lumière : la transmission reste intégrale grâce au champ de conscience unifié.",
                            },
                            {
                                n: "04",
                                icon: HeartHandshake,
                                title: "L'enregistrement & le suivi",
                                text: "La transmission est enregistrée : un lien vidéo vous est envoyé à l'issue de la séance pour réécouter le langage de lumière et les éventuels messages canalisés. Je reste disponible pour toute question dans les jours qui suivent.",
                            },
                        ].map((step, i) => (
                            <motion.li
                                key={step.n}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                                className="relative rounded-3xl bg-card border border-border/60 p-8 flex flex-col group hover:border-secondary/50 transition-colors"
                                data-testid={`step-${step.n}`}
                            >
                                <div className="flex items-baseline gap-3 mb-6">
                                    <span className="font-serif text-6xl text-secondary/40 leading-none">{step.n}</span>
                                    <step.icon className="w-5 h-5 text-primary" />
                                </div>
                                <h4 className="font-serif text-2xl mb-3">{step.title}</h4>
                                <p className="text-sm text-foreground/70 leading-relaxed">{step.text}</p>
                            </motion.li>
                        ))}
                    </ol>

                    <p className="text-center text-sm text-muted-foreground max-w-2xl mx-auto mt-12 leading-relaxed">
                        Ce qui est reçu dans les multi-dimensions de votre être est reçu quelle que soit votre posture —
                        seule l'expérience consciente de la séance peut varier.
                    </p>

                    {/* FAQ — SEO gold for AI answer engines (ChatGPT, Perplexity, Gemini) */}
                    <div className="mt-24 max-w-4xl mx-auto" data-testid="soins-faq">
                        <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Questions fréquentes</div>
                        <h3 className="font-serif text-3xl sm:text-4xl mb-10">Ce qu'on me demande souvent</h3>

                        <div className="space-y-8">
                            {guerisseur.faq.map((f, i) => (
                                <details key={i} className="group rounded-2xl border border-border/60 p-6 hover:border-primary/40 transition-colors" data-testid={`faq-item-${i}`}>
                                    <summary className="font-serif text-xl cursor-pointer flex items-center justify-between gap-4">
                                        <span>{f.q}</span>
                                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm shrink-0 group-open:rotate-45 transition-transform">+</span>
                                    </summary>
                                    <p className="text-foreground/75 leading-relaxed mt-4">{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <WellnessDisclaimer />
        </div>
    );
}
